import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

const METRIC_META = [
  { target: 99, sign: "+", key: "m1", testid: "metric-trazabilidad" },
  { target: 60, sign: "-", key: "m2", testid: "metric-fallas" },
  { target: 100, sign: "", key: "m3", testid: "metric-evidencia" },
  { target: 30, sign: "-", key: "m4", testid: "metric-costos" },
];

const DURATION = 1800; // ms — within the 1.5–2s window

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function AnimatedMetric({ target, sign, started }) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION, 1);
      const eased = easeOutCubic(progress);
      const current = Math.round(eased * target);
      setValue(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, target]);

  return (
    <span className="tabular-nums">
      {sign}
      {value}%
    </span>
  );
}

export default function AtlasMetrics() {
  const { t } = useI18n();
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    // Respect users that prefer reduced motion
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="casos"
      ref={sectionRef}
      data-testid="atlas-metrics"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center">
          <div className="h-1 w-20 rounded-full bg-sky-200" aria-hidden />
        </div>
        <h2
          data-testid="metrics-title"
          className="mt-6 text-center font-display text-3xl sm:text-4xl lg:text-[44px] leading-tight font-medium text-slate-900"
        >
          {t("metrics.qPre") && (
            <span className="font-normal text-slate-500">{t("metrics.qPre")}</span>
          )}
          <span className="font-bold">{t("metrics.qStrong1")}</span>{" "}
          <span className="font-normal text-slate-700">{t("metrics.qMid")}</span>{" "}
          <span className="font-bold">{t("metrics.qStrong2")}</span>
        </h2>

        <div className="mt-10 border-t border-slate-200" />

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-6">
          {METRIC_META.map((m) => (
            <div key={m.testid} data-testid={m.testid} className="text-center px-2">
              {/* Fixed-width wrapper keeps layout stable while digits change */}
              <div
                className="font-display text-5xl sm:text-6xl lg:text-[68px] font-extrabold tracking-tight text-[#2563EB] leading-none flex justify-center"
                style={{ minHeight: "1em" }}
              >
                <span
                  className="inline-block text-center"
                  style={{ minWidth: "4.5ch" }}
                  data-testid={`${m.testid}-value`}
                >
                  <AnimatedMetric target={m.target} sign={m.sign} started={started} />
                </span>
              </div>
              <div className="mt-3 text-slate-600 text-base lg:text-lg font-medium">
                {t(`metrics.${m.key}`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
